import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import { BellRing, CheckCheck, ChevronRight, Filter, MessageSquare, ShieldAlert, UserRound, Zap } from "lucide-react";
import api, { API_ORIGIN } from "../api";
import UserLayout from "../components/Userlayout";

const FILTERS = ["All", "Unread", "High Priority"];

const timeAgo = (d) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(d)) / 60000));
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr`;
  return `${Math.floor(h / 24)} d`;
};

const metaFor = (type) => {
  if (type === "quote_declined") return { Icon: ShieldAlert, box: "#ffe5e8", icon: "#e72f3e" };
  if (type === "quote_sent" || type === "quote_approved") return { Icon: Zap, box: "#e6f6ea", icon: "#31a24c" };
  if (type === "comment_new") return { Icon: MessageSquare, box: "#e8efff", icon: "#386cf3" };
  return { Icon: UserRound, box: "#eee9ff", icon: "#6744e8" };
};

export default function UserNotifications() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const socketRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!user?.id) return undefined;
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.emit("join", `user:${user.id}`);
    socket.on("notification:new", (n) => setItems((prev) => [n, ...prev]));
    return () => socket.disconnect();
  }, [user?.id]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => n._id === id ? { ...n, read: true } : n));
    try { await api.patch(`/notifications/${id}/read`); } catch { /* optimistic */ }
  };
  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await api.patch("/notifications/read-all"); } catch { /* optimistic */ }
  };
  const openNotification = (n) => {
    if (!n.read) markRead(n._id);
    if (n.link) navigate(n.link);
  };

  const filtered = useMemo(() => {
    if (filter === "Unread") return items.filter((n) => !n.read);
    if (filter === "High Priority") return items.filter((n) => n.priority === "high");
    return items;
  }, [items, filter]);

  const unread = items.filter((n) => !n.read).length;
  const today = filtered.filter((n) => new Date(n.createdAt).toDateString() === new Date().toDateString());
  const older = filtered.filter((n) => new Date(n.createdAt).toDateString() !== new Date().toDateString());

  const renderRows = (rows) => rows.map((n) => {
    const { Icon, box, icon } = metaFor(n.type);
    return (
      <button key={n._id} onClick={() => openNotification(n)} className="w-full rounded-[18px] bg-white px-8 py-6 text-left shadow-[0_1px_0_rgba(0,0,0,.02)] transition hover:-translate-y-[1px] hover:shadow-md">
        <div className="flex items-center gap-6">
          <span className="relative grid h-[74px] w-[74px] shrink-0 place-items-center rounded-[16px]" style={{ background: box, color: icon }}>
            <Icon size={34} strokeWidth={2} />
            {!n.read && <span className="absolute -bottom-1 -right-1 h-[18px] w-[18px] rounded-full border-2 border-white" style={{ background: icon }} />}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[21px] font-semibold text-[#172238]">{n.title || "Notification update"}</h3>
            <p className="mt-2 truncate text-[14px] text-[#858a94]">{n.message || "Open to view more details."}</p>
          </div>
          <div className="flex min-w-[150px] items-center justify-end gap-6">
            <div className="text-right">
              <div className="text-[13px] text-[#576072]">{timeAgo(n.createdAt)}</div>
              {n.priority === "high" && <div className="mt-8 text-[13px] font-semibold italic text-[#ed2637]">High Priority</div>}
            </div>
            <ChevronRight size={28} className="text-[#101b2b]" />
          </div>
        </div>
      </button>
    );
  });

  return (
    <UserLayout>
      <main className="min-h-[calc(100vh-92px)] bg-[#f7f7f9] pb-12 pt-5">
        <div className="agp-page-wrap">
          <div className="relative mb-5 flex w-fit items-center overflow-visible rounded-[12px] bg-white shadow-sm">
            <button onClick={markAllRead} className="flex h-[46px] items-center gap-3 border-r border-[#ececef] px-7 text-[14px] font-medium text-[#1c2638]"><CheckCheck size={22} className="text-[#12a15b]"/>Mark all as read</button>
            <button onClick={() => setShowFilterMenu((v) => !v)} className="flex h-[46px] items-center gap-3 px-8 text-[14px] font-medium text-[#1c2638]"><Filter size={21} className="text-[#12a15b]"/>Filter</button>
            {showFilterMenu && <div className="absolute left-[180px] top-[52px] z-20 w-44 rounded-xl border border-slate-100 bg-white p-1 shadow-xl">{FILTERS.map((f) => <button key={f} onClick={() => { setFilter(f); setShowFilterMenu(false); }} className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${filter === f ? "bg-slate-100 font-semibold" : "hover:bg-slate-50"}`}>{f}</button>)}</div>}
          </div>

          <section className="relative overflow-hidden rounded-[18px] bg-img2 px-[90px] py-[55px] text-white">
            <div className="relative z-10"><h1 className="text-[34px] font-bold">You have {unread} unread Notifications</h1><p className="mt-2 text-[15px] text-white/85">Tap to view and take action</p></div>
            {/* <BellRing size={130} className="absolute right-[90px] top-[28px] rotate-[-8deg] text-white/90" strokeWidth={1.6}/> */}
            {/* <span className="absolute right-[105px] top-[17px] h-[48px] w-[48px] rounded-full bg-[#ff453f]" /> */}
          </section>

          {loading ? <p className="py-20 text-center text-slate-400">Loading notifications…</p> : <>
            <section className="mt-5"><h2 className="mb-4 text-[14px] font-medium text-[#222]">Today</h2><div className="space-y-2">{today.length ? renderRows(today) : <div className="rounded-2xl bg-white p-8 text-center text-sm text-slate-400">No notifications today.</div>}</div></section>
            {older.length > 0 && <section className="mt-6"><h2 className="mb-4 text-[14px] font-medium text-[#222]">Older</h2><div className="space-y-2">{renderRows(older)}</div></section>}
          </>}
        </div>
      </main>
    </UserLayout>
  );
}
